import {
    CmsPlaceholder,
    FootnoteStyleSelector,
    ProductType,
} from '../enum';
import {
    FootnoteHtmlContent,
    JarvisPrice,
    JarvisTrackingType,
    JarvisTvProduct,
} from '../interface';
import {
    getCMSConfig,
    getLegalDiscountText,
    isYoungPromoActive,
} from './cms';

/**
 * Convert any number or string into a string which can be displayed on frontend
 *
 * @param price - Price to convert.
 * @param showEmptyCents - Show the ",00" part? (default=false)
 * @returns Price string.
 */
export const convertToPriceString = (price: number | string, showEmptyCents = false) => {
    const priceNumber = +(price.toString().replace(',', '.'));

    return priceNumber.toFixed(!showEmptyCents && Number.isInteger(priceNumber) ? 0 : 2).replace('.', ',');
};

/**
 * Convert any number into a string which can be displayed in the tracking payload
 *
 * @param price - Price to convert.
 * @param feeType - Type of a product or option.
 * @returns Price string for tracking.
 */
export const convertToTrackingPrice = (price: number, feeType: ProductType | JarvisTrackingType) =>
    `${(feeType === 'discount') ? '-' : ''}${Math.abs(price).toFixed(2)}`;

/**
 * - formats the given string to lowercase
 * - separates words if they are next to each other without space
 * - removes any additional whitespaces
 * - replaces the special German letters in the text to basic latin letters
 *
 * Note: This step is requested by Digital Power, to process the incoming data more easily.
 *
 * @param text - Text to format into a tracking text.
 * @param maxLength - Maximum length of the text.
 * @returns Formatted string representation of a string,
 * which can be used in a tracking payload.
 */
export const toTrackingString = (text: string, maxLength = 100) =>
    text.replace(/([a-z])([A-Z])/g, '$1 $2')
        .toLowerCase()
        .trim()
        .replace(/\s+/g, ' ')
        .substring(0, maxLength)
        .replace(/\u00e4/g, 'ae')
        .replace(/\u00f6/g, 'oe')
        .replace(/\u00fc/g, 'ue')
        .replace(/\u00df/g, 'ss');

/**
 * Calculates discounted price
 * Note: prices below 0 will be turned into 0
 *
 * @param price - Original price
 * @param discount - Discount
 * @returns The price in correct number format
 */
export const getDiscountedPrice = (price: number, discount = 0) =>
    Number(Math.max(0, (price - Math.abs(discount))).toFixed(2));

/**
 * Creates formatted legal discount text
 */
export const createLegalDiscountText = (friendsAndFamilyDiscount: number, minorityNE4Discount: number) => {
    const text = getLegalDiscountText(Boolean(friendsAndFamilyDiscount), Boolean(minorityNE4Discount));

    if (!text) {
        return null;
    }

    // TODO: make unique placeholder for each price/discount! -> do not replace by order!
    let newText = text;

    if (minorityNE4Discount) {
        newText = newText.replace(CmsPlaceholder.PRICE, convertToPriceString(Math.abs(minorityNE4Discount)));
    }

    if (friendsAndFamilyDiscount) {
        newText = newText.replace(CmsPlaceholder.PRICE, convertToPriceString(Math.abs(friendsAndFamilyDiscount)));
    }

    return newText;
};

/**
 * Creates formatted minority NE4 discount notification text if discount is available
 */
export const createNE4NotificationText = (minorityNE4Discount?: number) => {
    if (!minorityNE4Discount) {
        return '';
    }

    const ne4Text = getCMSConfig().avcResult.cable.available.ne4Text;
    const discountPriceString = convertToPriceString(Math.abs(minorityNE4Discount));

    return ne4Text.replace(CmsPlaceholder.PRICE, discountPriceString);
};

/**
 * Creates start bonus text
 *
 * @param price - Price to be shown in start bonus text
 */
export const createStartBonusText = (price: JarvisPrice) =>
    getCMSConfig().offerSummaryCard.highlightBadge?.text
        .replace(CmsPlaceholder.PRICE, convertToPriceString(Math.abs(price.value)));

/**
 * Creates tv picker text
 *
 * @param price - Price to be shown above tv picker
 * @param productName - TV product name to show
 */
export const createTvPickerText = (price: number, productName: string) =>
    getCMSConfig().tvPickerText
        .replace(CmsPlaceholder.PRICE, convertToPriceString(Math.abs(price)))
        .replace(CmsPlaceholder.PRODUCT, productName);

/**
 * Creates young picker text
 *
 * @param price - Price to be shown above picker picker (Best choice)
 * @param promoPrice - Promo price to be shown above picker picker (Young promo)
 */
export const createYoungPickerText = (price: number, promoPrice?: number) =>
    promoPrice && isYoungPromoActive() ?
        getCMSConfig().youngPromoPickerText
            .replace(CmsPlaceholder.PRICE, convertToPriceString(Math.abs(price)))
            .replace(CmsPlaceholder.PROMO_PRICE, convertToPriceString(Math.abs(promoPrice)))
        :
        getCMSConfig().youngPickerText
            .replace(CmsPlaceholder.PRICE, convertToPriceString(Math.abs(price)));

/**
 * Creates duration text
 *
 * @param duration - Duration to be shown in text
 */
export const createDurationText = (duration: number | string) =>
    getCMSConfig().offerSummaryCard.additionalText.duration
        .replace(CmsPlaceholder.DURATION, duration.toString());

const formatFootnoteContent = (text: string, tvTariff: JarvisTvProduct) => text
    .replaceAll(CmsPlaceholder.PRICE, convertToPriceString(tvTariff.price.value))
    .replaceAll(CmsPlaceholder.DISCOUNTED_PRICE, convertToPriceString(tvTariff.discountedPrice.value))
    .replaceAll(CmsPlaceholder.DURATION, tvTariff.duration.toString());

/**
 * Creates the footnote content and title
 *
 * @param htmlText  - Fetched HTML based on the available TV Connect
 * @param tariff  - The available TV Connect
 * @returns Content and title of the footnote
 */
export const createFootnoteContent = (htmlText: string, tariff: JarvisTvProduct): FootnoteHtmlContent | undefined => {
    const tempEl = document.createElement('div');

    tempEl.innerHTML = formatFootnoteContent(htmlText, tariff);

    const [accordionTitleEl] = tempEl.getElementsByClassName(FootnoteStyleSelector.ACCORDION_TITLE);
    const [contentTitleEl] = tempEl.getElementsByClassName(FootnoteStyleSelector.CONTENT_TITLE);
    const [contentEl] = tempEl.getElementsByClassName(FootnoteStyleSelector.CONTENT);

    if (!accordionTitleEl || !contentTitleEl || !contentEl) {
        return undefined;
    }

    return {
        accordionTitle: accordionTitleEl.innerHTML,
        contentTitle: contentTitleEl.innerHTML,
        content: contentEl.innerHTML,
    };
};

/**
 * Returns text with formatted product name.
 *
 * @param text - a text with a placeholder
 * @param productName - the value of the placeholder
 * @returns string
 */
export const formatProductName = (text: string, productName?: string) =>
    text.replace(CmsPlaceholder.PRODUCT, productName || '');

/**
 * Returns text with formatted download speed.
 *
 * @param text - a text with a placeholder
 * @param download - the value of the placeholder
 * @returns string
 */
export const formatDownload = (text: string, download?: string | number) =>
    text.replace(CmsPlaceholder.DOWNLOAD, download?.toString() || '');

/**
 * Returns text with formatted price.
 *
 * @param text - a text with a placeholder
 * @param price - the value of the placeholder
 * @returns string
 */
export const formatPrice = (text: string, price: string | number) =>
    text.replace(CmsPlaceholder.PRICE, convertToPriceString(price));
